<template>
  <div class="h-full flex flex-col p-10">
    <!-- 회사명 검색 섹션 -->
    <div v-if="!store.analysisData" class="flex-1 flex flex-col overflow-hidden">

      <!-- 탭 선택: 자동 검색 vs 파일 업로드 -->
      <div class="border-t-4 border-dark-600 top-0 z-20 mb-6 flex -mx-5 px-5 flex-shrink-0">
        <button
          :class="[
            'px-8 py-4 font-semibold text-base transition-all duration-200 border-b-4 relative',
            activeTab === 'search'
              ? 'border-primary text-white bg-dark-700'
              : 'border-transparent text-gray-400 hover:text-white hover:bg-dark-700'
          ]"
          @click="handleTabChange('search')"
        >
          <i class="bi bi-search mr-2"></i>
          자산운용사 검색
        </button>
        <button
          :class="[
            'px-8 py-4 font-semibold text-base transition-all duration-200 border-b-4 relative',
            activeTab === 'upload'
              ? 'border-primary text-white bg-dark-700'
              : 'border-transparent text-gray-400 hover:text-white hover:bg-dark-700'
          ]"
          @click="handleTabChange('upload')"
        >
          <i class="bi bi-cloud-upload mr-2"></i>
          파일 업로드
        </button>
      </div>

      <!-- 자동 검색 탭 -->
      <div v-if="activeTab === 'search'" class="flex-1 flex flex-col overflow-hidden">
        <!-- 입력 영역 (회사명 검색 + 테이블) -->
        <div class="flex-1 flex flex-col overflow-hidden min-h-0 card">
          <label class="input-label">
            <i class="bi bi-search mr-2 text-primary"></i>
            회사명 검색
          </label>
          <div class="flex gap-3 mb-8">
            <input
              v-model="searchCompanyName"
              type="text"
              placeholder="자산운용사명을 검색하세요 (예: 미래에셋자산운용)"
              class="input flex-1"
            />
          </div>

          <!-- 자산운용사 목록 테이블 -->
          <div v-if="companiesData.length > 0" class="flex-1 flex flex-col overflow-hidden min-h-0">
            <div class="flex items-center justify-between mb-4 flex-shrink-0">
              <p class="text-base text-gray-400">
                 총 <span class="text-white font-semibold">{{ companiesData?.length || 0 }}</span>개 
                 <span v-if="selectedCompany" class="ml-4 inline-flex items-center">
                   <i class="bi bi-check-circle text-primary mr-2"></i>
                   <span class="text-primary font-medium">{{ selectedCompany }} 선택됨</span>
                 </span>
              </p>
            </div>

            <div class="flex-1 bg-dark-700 border border-dark-600 rounded-lg overflow-hidden flex flex-col min-h-0">
              <div class="flex-1 overflow-y-auto">
                <table class="w-full table-fixed">
                  <colgroup>
                    <col class="w-40" />
                    <col class="w-48" />
                    <col class="flex-1" />
                  </colgroup>
                  <thead class="bg-dark-600 border-dark-500 sticky top-0 z-10">
                    <tr>
                       <th class="px-4 py-4 text-center">
                         <span class="text-xs text-gray-500">선택</span>
                       </th>
                      <th class="px-4 py-4 text-center text-sm font-semibold text-gray-400 uppercase tracking-wider">번호</th>
                      <th class="px-4 py-4 text-center text-sm font-semibold text-gray-400 uppercase tracking-wider">자산운용사명</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-dark-600">
                    <tr
                      v-for="(company, index) in filteredCompanies"
                      :key="index"
                      :class="[
                        'hover:bg-dark-600 transition-colors cursor-pointer group h-14',
                        isCompanySelected(company.company_name) ? 'bg-dark-600 bg-opacity-80' : ''
                      ]"
                      @click="toggleCompanySelection(company.company_name)"
                    >
                       <td class="px-4 py-3 text-center">
                         <input
                           type="radio"
                           :checked="isCompanySelected(company.company_name)"
                           @click.stop="toggleCompanySelection(company.company_name)"
                           class="w-4 h-4 cursor-pointer"
                           name="companySelection"
                         />
                       </td>
                      <td class="px-4 py-3 text-center text-base text-gray-500 font-semibold">{{ String(index + 1).padStart(2, '0') }}</td>
                      <td class="px-4 py-3 text-center text-base text-gray-200 truncate group-hover:text-white transition-colors">{{ company.company_name }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 분석 시작 버튼 -->
        <div class="flex-shrink-0 mt-8">
          <button
            :disabled="store.isLoading || !selectedCompany"
            @click="handleFindReport"
            class="w-full btn-primary mb-3"
          >
            <span v-if="!store.isLoading">
              <i class="bi bi-play-fill mr-2"></i>분석 시작
            </span>
            <span v-else>
              <i class="bi bi-hourglass-split mr-2 animate-pulse"></i>
              분석 중...
            </span>
          </button>

          <!-- 진행 상황 표시 -->
          <div v-if="store.isLoading && progressMessage" class="mt-4 p-4 bg-dark-700 border border-dark-600 rounded-lg">
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-gray-400">분석 진행 상황</span>
              <span class="text-sm text-primary font-semibold">{{ progressPercent }}%</span>
            </div>
            <div class="w-full bg-dark-600 rounded-full h-2 mb-3">
              <div 
                class="bg-gradient-to-r from-primary to-emerald-400 h-2 rounded-full transition-all duration-500 ease-out"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
            <div class="flex items-center gap-2 text-base">
              <i class="bi bi-arrow-right-circle text-primary animate-pulse"></i>
              <span class="text-gray-200">{{ progressMessage }}</span>
            </div>
            
            <!-- 진행 로그 -->
            <div v-if="progressLogs.length > 0" class="mt-4 max-h-32 overflow-y-auto">
              <div 
                v-for="(log, index) in progressLogs" 
                :key="index"
                class="text-sm text-gray-500 py-1 border-b border-dark-600 last:border-b-0"
              >
                {{ log }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 파일 업로드 탭 -->
      <div v-else class="flex-1 flex flex-col overflow-hidden">
        <!-- 입력 영역 (회사명 + 파일 업로드) -->
        <div class="flex-1 flex flex-col overflow-hidden min-h-0 card">
          <!-- 회사명 입력 -->
          <div class="mb-8 flex-shrink-0">
            <label class="input-label">
              <i class="bi bi-building mr-2 text-primary"></i>
              회사명 입력
            </label>
            <input
              v-model="uploadCompanyName"
              type="text"
              placeholder="자산운용사명을 입력하세요(예: 미래에셋자산운용)"
              class="input w-full"
            />
          </div>

          <!-- 파일 업로드 -->
          <div class="flex-1 flex flex-col overflow-hidden min-h-0">
            <label class="input-label flex-shrink-0">
              <i class="bi bi-cloud-arrow-up mr-2 text-primary"></i>
              파일 업로드
            </label>
            
            <div class="border border-dark-600 rounded-lg p-6 flex-1 flex flex-col overflow-hidden min-h-0 gap-4">
              <!-- 파일 드래그 영역 -->
              <div
                @drop="handleDrop"
                @dragover.prevent
                @dragenter.prevent
                @dragleave="isDragging = false"
                :class="[
                  'border-2 border-dashed rounded-lg p-8 py-16 text-center cursor-pointer transition-colors duration-200 flex-1 flex items-center justify-center min-h-0',
                  isDragging
                    ? 'border-primary bg-dark-700'
                    : 'border-dark-600 hover:border-primary'
                ]"
                @click="$refs.fileInput.click()"
                @dragenter="isDragging = true"
              >
                <div>
                  <i class="bi bi-file-earmark-pdf text-4xl text-gray-400 mb-3 block"></i>
                  <p class="text-lg font-bold text-gray-200 mb-1">3개년 영업보고서</p>
                  <p class="text-primary text-sm mb-1">파일을 드래그 하거나 클릭하여 업로드 해주세요</p>
                  <p class="text-caption mt-4">PDF 형식의 파일을 선택해 주세요</p>
                </div>
              </div>

              <input
                ref="fileInput"
                type="file"
                multiple
                accept=".pdf"
                style="display: none"
                @change="handleFileSelect"
              />

              <!-- 선택된 파일 목록 -->
              <div v-if="store.uploadedFiles && store.uploadedFiles.length > 0" class="bg-dark-700 border border-dark-600 rounded-lg p-6 flex-1 flex flex-col overflow-hidden min-h-0">
                <div class="flex items-center justify-between mb-3 flex-shrink-0">
                  <p class="text-sm text-gray-400">
                    선택된 파일 ({{ store.uploadedFiles?.length || 0 }}개)
                  </p>
                </div>
                <div class="flex-1 overflow-y-auto min-h-0">
                  <div class="space-y-3">
                    <div v-for="(file, index) in store.uploadedFiles" :key="index" class="flex items-center justify-between p-3 rounded-lg hover:bg-dark-600 transition-colors">
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <div class="flex-shrink-0 w-8 h-8 rounded-full bg-red-900 bg-opacity-30 flex items-center justify-center">
                          <span class="text-red-300 font-bold text-sm">{{ index + 1 }}</span>
                        </div>
                        <i class="bi bi-file-pdf text-red-500 text-xl flex-shrink-0"></i>
                        <span class="text-white text-base truncate">{{ file.name }}</span>
                      </div>
                      <button
                        @click="store.removeFile(index)"
                        class="text-gray-400 hover:text-red-500 transition-colors ml-3 flex-shrink-0"
                      >
                        <i class="bi bi-trash text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="bg-dark-700 border border-dark-600 rounded-lg p-6 flex-1 flex items-center justify-center min-h-0">
                <div class="text-center">
                  <i class="bi bi-inbox text-gray-600 text-4xl mb-2 block"></i>
                  <p class="text-gray-500 text-sm">선택된 파일이 없습니다</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 버튼들 -->
        <div class="flex-shrink-0 mt-8">
          <button
            :disabled="store.isLoading || !uploadCompanyName || !store.uploadedFiles || store.uploadedFiles.length === 0"
            @click="handleUploadReports"
            class="w-full btn-primary mb-3"
          >
            <span v-if="!store.isLoading">
              <i class="bi bi-play-fill mr-2"></i>분석 시작 ({{ store.uploadedFiles?.length || 0 }}개 파일)
            </span>
            <span v-else>
              <i class="bi bi-hourglass-split mr-2 animate-pulse"></i>
              분석 중...
            </span>
          </button>

          <button
            :disabled="store.isLoading"
            @click="handleLoadTestData"
            class="w-full btn-secondary mb-4"
          >
            <i class="bi bi-file-earmark-arrow-down mr-2"></i>
            검증 데이터 로드하기
          </button>
        </div>
      </div>
    </div>

    <!-- 분석 결과 표시 -->
    <div v-else>
      <EvaluationResult />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useAssetManagementStore } from '@/stores/assetManagement';
import { assetManagementService } from '@/services/assetManagement';
import EvaluationResult from '@/components/asset-management/EvaluationResult.vue';
import { useToast } from 'vue-toastification';
import companiesDataRaw from '@/assets/companies.json';

const store = useAssetManagementStore();
const toast = useToast();

const activeTab = ref('search');
const searchCompanyName = ref('');
const uploadCompanyName = ref('');
const isDragging = ref(false);
const fileInput = ref(null);

// companies.json 데이터 로드
const companiesData = ref([]);
const selectedCompany = ref(null); // 단일 선택으로 변경

// 진행 상황 관련
const progressMessage = ref('');
const progressPercent = ref(0);
const progressLogs = ref([]);

// 페이지 마운트 시 데이터 초기화
onMounted(() => {
  // companies.json 데이터 로드
  if (companiesDataRaw && companiesDataRaw.companies) {
    companiesData.value = companiesDataRaw.companies;
    console.log('자산운용사 데이터 로드 완료:', companiesData.value.length + '개');
  } else if (companiesDataRaw && companiesDataRaw['시트1']) {
    companiesData.value = companiesDataRaw['시트1'];
    console.log('자산운용사 데이터 로드 완료:', companiesData.value.length + '개');
  } else {
    console.error('companies.json 데이터 형식이 올바르지 않습니다.');
  }
});

// 회사명 필터링 - computed 사용
const filteredCompanies = computed(() => {
  if (!searchCompanyName.value.trim()) {
    // 검색어가 없으면 전체 목록 표시 (최대 50개)
    return companiesData.value
  }
  
  // 검색어가 있으면 필터링
  return companiesData.value
    .filter(company =>
      company.company_name.toLowerCase().includes(searchCompanyName.value.toLowerCase())
    )
    .slice(0, 50); // 최대 50개만 표시
});

// 회사 선택 (1개만)
const toggleCompanySelection = (companyName) => {
  if (selectedCompany.value === companyName) {
    selectedCompany.value = null; // 이미 선택된 경우 해제
  } else {
    selectedCompany.value = companyName; // 새로운 회사 선택
  }
};

// 선택 여부 확인
const isCompanySelected = (companyName) => {
  return selectedCompany.value === companyName;
};

// 탭 변경 시 리셋
const handleTabChange = (tab) => {
  // 선택 값들 초기화
  selectedCompany.value = null;
  searchCompanyName.value = '';
  uploadCompanyName.value = '';
  
  // 파일 업로드 관련 초기화
  store.clearFiles(); // 업로드된 파일 모두 제거
  isDragging.value = false;
  
  // 탭 변경
  activeTab.value = tab;
};

const handleFindReport = async () => {
  if (!selectedCompany.value) {
    toast.error('자산운용사를 선택해주세요');
    return;
  }

  // 분석 시작 안내 메시지
  toast.info('AI 분석을 시작합니다.', {
    timeout: 3000,
  });

  store.setLoading(true);
  store.setError(null);
  
  // 진행 상황 초기화
  progressMessage.value = '분석 준비 중...';
  progressPercent.value = 0;
  progressLogs.value = [];

  console.log('선택된 회사:', selectedCompany.value);
  
  // SSE 스트리밍으로 분석 수행
  assetManagementService.findReportStream(
    selectedCompany.value,
    // onProgress 콜백
    (data) => {
      progressMessage.value = data.message;
      progressPercent.value = data.progress;
      progressLogs.value.unshift(data.message); // 최신 로그를 위에 추가
      
      // 최대 10개 로그만 유지
      if (progressLogs.value.length > 10) {
        progressLogs.value.pop();
      }
    },
    // onComplete 콜백
    (data) => {
      console.log('분석 결과:', data);
      store.setAnalysisData(data);
      store.setLoading(false);
      progressMessage.value = '';
      progressPercent.value = 0;
      progressLogs.value = [];
      toast.success('분석이 완료되었습니다.');
    },
    // onError 콜백
    (error) => {
      console.error('분석 오류:', error);
      store.setLoading(false);
      progressMessage.value = '';
      progressPercent.value = 0;
      progressLogs.value = [];
      
      // 크롤링 실패 시
      if (error.status === 'crawl_failed') {
        toast.warning('웹에서 파일을 찾을 수 없습니다. 직접 파일을 업로드해주세요.');
        searchCompanyName.value = '';
        selectedCompany.value = null;
        activeTab.value = 'upload';
        uploadCompanyName.value = '';
      } else {
        const errorMessage = error.message || '분석 중 오류가 발생했습니다. 다시 시도해주세요.';
        store.setError(errorMessage);
        toast.error('분석 실패: ' + errorMessage);
        
        searchCompanyName.value = '';
        selectedCompany.value = null;
        activeTab.value = 'upload';
        uploadCompanyName.value = '';
      }
    }
  );
};

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files);
  const currentCount = store.uploadedFiles?.length || 0;
  
  if (currentCount + files.length > 12) {
    toast.warning(`최대 12개의 파일만 선택할 수 있습니다. (현재 ${currentCount}개 선택됨)`);
    return;
  }
  
  store.addFiles(files);
  toast.success(`${files.length}개 파일이 추가되었습니다.`);
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragging.value = false;
  
  const files = Array.from(event.dataTransfer.files).filter(file => 
    file.type === 'application/pdf'
  );
  
  if (files.length === 0) {
    toast.warning('PDF 파일만 업로드 가능합니다.');
    return;
  }
  
  const currentCount = store.uploadedFiles?.length || 0;
  
  if (currentCount + files.length > 12) {
    toast.warning(`최대 12개의 파일만 선택할 수 있습니다. (현재 ${currentCount}개 선택됨)`);
    return;
  }
  
  store.addFiles(files);
  toast.success(`${files.length}개 파일이 추가되었습니다.`);
};

const handleUploadReports = async () => {
  if (!uploadCompanyName.value) {
    toast.error('회사명을 입력해주세요');
    return;
  }

  if (store.uploadedFiles.length === 0) {
    toast.error('파일을 선택해주세요');
    return;
  }

  // 분석 시작 안내 메시지
  toast.info('AI 분석을 시작합니다. 분석에는 최대 10분 정도 소요될 수 있습니다.', {
    timeout: 5000,
  });

  store.setLoading(true);
  store.setError(null);

  try {
    const response = await assetManagementService.uploadReports(
      uploadCompanyName.value,
      store.uploadedFiles
    );

    // 최종 반환값을 store에 저장
    if (response.data) {
      console.log('분석 결과:', response.data);
      store.setAnalysisData(response.data);
      toast.success('분석이 완료되었습니다.');
    } else {
      throw new Error('분석 결과 데이터가 없습니다.');
    }
  } catch (error) {
    console.error('분석 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '분석 중 오류가 발생했습니다. 다시 시도해주세요.';
    store.setError(errorMessage);
    toast.error('분석 실패: ' + errorMessage);
  } finally {
    store.setLoading(false);
  }
};

const handleLoadTestData = async () => {
  try {
    // import로 JSON 파일 직접 가져오기
    const testData = await import('@/assets/results/asset_result.json');
    const data = testData.default;

    if (data) {
      store.setAnalysisData(data);
      toast.success('검증 데이터가 로드되었습니다.');
    }
  } catch (error) {
    console.error(error);
    toast.error('데이터 로드 실패: ' + error.message);
  }
};


// 페이지 언마운트 시 상태 정리
onUnmounted(() => {
  // 로딩 상태 초기화 (결과는 유지)
  if (store.isLoading) {
    store.setLoading(false);
  }
});
</script>

<style scoped>
</style>
